import { bypassRefreshResponse } from '@/mixins/RequestMixins/RequestMixins.js'
import { eventBus } from '@/plugins/eventbus.ts'
import router from '@/router'
import endpoints from '@/utils/endpoints.js'
import axios, { anonymousAxios } from '@/utils/init-axios.js'
import moment from 'moment'
import { session, getSessionIdToken } from '@/utils/session'
import {
  clearSessionProviderPin,
  pinSessionProvider,
  usesFirebaseSession
} from '@/utils/session-config'
import { clearIntegrationTypeCookie } from '@/utils/cookies'

const timeoutSeconds = 60 * process.env.VUE_APP_SESSION_TIMEOUT
const idleSeconds = 60 * process.env.VUE_APP_SESSION_IDLE

let refreshPromise = null

async function fetchNewTokens(refreshToken) {
  const response = await anonymousAxios.post(
    endpoints.createTokenRefresh(),
    { refresh: refreshToken },
    { transformResponse: [bypassRefreshResponse] }
  )

  return response
}

export default {
  async login({ commit, dispatch }, credentials) {
    if (usesFirebaseSession())
      throw new Error('Use Firebase entry for this session')
    const endpoint =
      credentials.id && credentials.token
        ? endpoints.SSOAuth()
        : endpoints.createBlytzTokenObtainPair()

    const response = await anonymousAxios.post(endpoint, credentials)
    const data = response.data

    const accessToken = data.access
    const refreshToken = data.refresh
    const firebaseToken = data.firebase_token ?? null

    pinSessionProvider('legacy')
    commit('setTokens', { accessToken, refreshToken, firebaseToken })

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

    await dispatch('heartbeat')
  },

  async logout({ state, commit, getters }) {
    if (usesFirebaseSession()) return session.end({ explicit: true })
    eventBus.emit('clearInterval', {})

    try {
      if (getters.isAuthenticated) {
        await axios.post(endpoints.blacklistToken(), {
          refresh_token: state.refreshToken
        })
      }
    } catch {
      // If there was a problem, it's meaningless to everyone, so
      // don't let them escape to Sentry
    }

    clearInterval(state.heartbeat)
    localStorage.removeItem('current_merchant_id')
    commit('setHeartbeat', null)
    commit('setTokens', {
      accessToken: null,
      refreshToken: null,
      firebaseToken: null
    })
    commit('setIsInactive', false)
    commit('setReceiptModalVisibility', false)
    commit('resetState')
    commit('resetUserPreferences')
    clearIntegrationTypeCookie()
    clearSessionProviderPin('legacy')
    axios.defaults.headers.common = {}

    if (router.currentRoute.name !== 'Login') {
      router.push({ name: 'Login' })
    }
  },

  async heartbeat({ commit, state, dispatch }) {
    if (state.heartbeat) return

    const heartbeat = setInterval(() => {
      dispatch('authHeartbeat')
    }, process.env.VUE_APP_HEARTBEAT_MS)

    commit('setHeartbeat', heartbeat)
  },

  async refreshAccess({ commit, state, getters, dispatch }) {
    if (usesFirebaseSession()) return getSessionIdToken(true)
    if (refreshPromise) {
      return refreshPromise
    }
    if (!state.refreshToken) {
      return
    }

    const claims = getters.refreshClaims
    const now = moment().utc().unix()
    if (now >= claims?.exp) {
      return
    }

    refreshPromise = (async () => {
      try {
        const response = await fetchNewTokens(state.refreshToken)
        const data = response.data
        commit('setTokens', {
          accessToken: data.access,
          refreshToken: data.refresh || state.refreshToken,
          firebaseToken: data.firebase_token ?? null
        })
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access}`

        return response
      } catch (error) {
        if (!usesFirebaseSession()) dispatch('logout')
        throw error
      } finally {
        refreshPromise = null
      }
    })()

    return refreshPromise
  },

  // Handles idle logout. Should be called every 15 seconds or so
  async authHeartbeat({ commit, getters, state, dispatch }) {
    if (usesFirebaseSession()) {
      session.checkIdle()

      return
    }
    // If we are not authenticated, then bail
    if (!getters.isAuthenticated) return

    const now = moment().utc().unix()
    const sinceLastActivity = now - state.lastActivity
    const idle = idleSeconds < sinceLastActivity

    if (timeoutSeconds < sinceLastActivity) {
      // Log out if session has timed out
      dispatch('logout')
    } else if (idleSeconds < sinceLastActivity && state.isInactive == false) {
      commit('setIsInactive', idle)
    } else if (
      0 < getters.refreshTokenTimeToLive() &&
      getters.accessTokenTimeToLive() < 180
    ) {
      /*
        Refresh access if following conditions are met
        - Refresh token is valid
        - Access token has less than 50 seconds to live
      */
      dispatch('refreshAccess')
    }
  },

  // Called when the user chooses to continue their session
  continueSession({ dispatch, commit }) {
    if (usesFirebaseSession()) {
      session.humanActivity()

      return
    }
    commit('setIsInactive', false)
    commit('setLastActivity')
    dispatch('refreshAccess')
  },

  refreshSessionAuthorization() {
    return session.refreshAuthorization()
  },

  async getProfile({ commit }) {
    const generation = session.generation
    const response = await axios.get(endpoints.listProfiles())
    const profile = response?.data?.results[0]

    if (usesFirebaseSession()) session.assertCurrent(generation)
    commit('setProfile', profile)

    return response
  }
}
